import { CellColor, CellVariant } from 'pages/app/billing/invoices/invoices-table/cell/types.ts'
import { getStatus } from 'pages/app/billing/invoices/invoices-table/utils.ts'
import i18n from 'translations/i18next.ts'
import dayjs from 'dayjs'
import { formatCurrency, formatInvoiceDownloadUrl } from 'utils/format.ts'
import Link from 'components/link/Link.tsx'
import Cell from 'pages/app/billing/invoices/invoices-table/cell/Cell.tsx'
import AccountingInfo from 'pages/app/billing/invoices/invoices-table/accounting-info/AccountingInfo.tsx'
import i18next from 'i18next'
import Options from 'pages/app/billing/invoices/invoices-table/options/Options.tsx'
import {
  GetTableData,
  InvoiceCells,
  InvoiceTableDataRow,
  InvoiceTableEntry,
  RegularTableCells,
  SuperUserTableCells,
  TransactionRows
} from './types.ts'
import { getAddressByDocumentContractNumber, isCustomerInvoice, isCustomerPayment } from 'pages/app/billing/invoices/utils.ts'
import { StatusReturnValue } from 'pages/app/billing/invoices/invoices-table/types.ts'
import { INVOICE_TYPE_TRANSLATION_KEYS } from 'pages/app/billing/invoices/constants.ts'
import { Button, Dropdown, DropdownLinkItem } from '@boltenergy-be/design-system'
import styles from './TableData.module.scss'
import parse from 'html-react-parser'
import { trackEvent } from 'utils/tracking.ts'
import { CustomerAccountingDocument, CustomerInvoice, CustomerPayment, Language } from '@boltenergy-be/shared'

/**
 * Format the date entry data
 * @param {CustomerAccountingDocument} document
 * @returns {InvoiceTableEntry}
 */
const getDateCell = (document: CustomerAccountingDocument): InvoiceTableEntry<InvoiceCells.DATE> => {
  return {
    header: i18n.t(($) => $.tableHeaders.date, { ns: 'invoices' }),
    rawData: document.date,
    content: dayjs(document.date).format('DD/MM/YYYY')
  }
}

/**
 * Format the date entry data
 * @param {CustomerAccountingDocument} document
 * @returns {InvoiceTableEntry}
 */
const getInvoiceNumberCell = (document: CustomerAccountingDocument): InvoiceTableEntry<InvoiceCells.INVOICE_NUMBER> => {
  return {
    hidden: true, // Hidden because it's only being used in the modal
    rawData: 'invoiceNumber' in document ? document.invoiceNumber : null,
    content: '',
    header: ''
  }
}

/**
 * Format the amount entry data with status text display
 * @param {CustomerAccountingDocument} document
 * @param {StatusReturnValue} status
 * @param {Language} language
 * @param {boolean} showAddress
 * @param {boolean} isTabletOrMobile
 * @returns {InvoiceTableEntry}
 */
const getAmountCell = (
  document: CustomerAccountingDocument,
  status: StatusReturnValue,
  language: Language,
  showAddress?: boolean,
  isTabletOrMobile?: boolean
): InvoiceTableEntry<InvoiceCells.AMOUNT> => {
  return {
    header: i18n.t(($) => $.tableHeaders.amount, { ns: 'invoices' }),
    rawData: document.amount,
    content: (
      <Cell variant={CellVariant.PAYMENT_AMOUNT} color={status.color}>
        {parse(formatCurrency(document.amount, { language }).replace('-', '&minus;&nbsp;'))} {isTabletOrMobile ? `· ${status.value}` : ''}
      </Cell>
    ),
    ...(isTabletOrMobile && {
      extraClasses: styles['combined-cell'],
      extraContentCellKey: isCustomerInvoice(document) && showAddress ? InvoiceCells.ADDRESS : undefined
    })
  }
}

/**
 * Format the status entry data
 *
 * @param {CustomerAccountingDocument} document
 * @param {StatusReturnValue} status
 * @param {boolean} [showAddress]
 * @param {boolean} [isTabletOrMobile]
 * @param {number} [colSpan]
 * @returns {InvoiceTableEntry}
 */
const getStatusCell = (
  document: CustomerAccountingDocument,
  status: StatusReturnValue,
  showAddress?: boolean,
  isTabletOrMobile?: boolean,
  colSpan?: number
): InvoiceTableEntry<InvoiceCells.STATUS> => {
  return {
    hidden: !!isTabletOrMobile,
    header: i18n.t(($) => $.tableHeaders.status, { ns: 'invoices' }),
    rawData: status.value,
    colSpan,
    content: (
      <Cell variant={CellVariant.STATUS} color={status.color}>
        {parse(status.value)}
      </Cell>
    ),
    ...(!isTabletOrMobile && {
      extraClasses: styles['combined-cell'],
      extraContentCellKey: isCustomerInvoice(document) && showAddress ? InvoiceCells.ADDRESS : undefined
    })
  }
}

/**
 * Format the doc type entry data
 * @param {CustomerAccountingDocument} document
 * @returns {InvoiceTableEntry}
 */
const getDocTypeCell = (document: CustomerAccountingDocument): InvoiceTableEntry<InvoiceCells.DOCUMENT_TYPE> => {
  const isPayment = isCustomerPayment(document)

  return {
    header: i18n.t(($) => $.tableHeaders.documentType, { ns: 'invoices' }),
    rawData: isPayment ? document.documentType : document.invoiceType,
    content: parse(
      (isPayment
        ? i18next.t(($) => $.cells.bankTransaction, {
            ns: 'invoices'
          })
        : INVOICE_TYPE_TRANSLATION_KEYS[document.invoiceType] && INVOICE_TYPE_TRANSLATION_KEYS[document.invoiceType] !== 'exclude'
          ? i18next.t(($) => $.invoiceTypes[INVOICE_TYPE_TRANSLATION_KEYS[document.invoiceType]], { ns: 'invoices' })
          : ''
      ).replaceAll(' ', '&nbsp;')
    )
  }
}

/**
 * Format the doc type entry data
 * @param {CustomerAccountingDocument} document
 * @returns {InvoiceTableEntry}
 */
const getAddressCell = (document: CustomerAccountingDocument): InvoiceTableEntry<InvoiceCells.ADDRESS> => {
  const address = getAddressByDocumentContractNumber({ document })
  return {
    hidden: true,
    header: '',
    rawData: address,
    content: address
  }
}

/**
 * Get the  invoice table data for a regular customer
 * @template DocumentTypes - CustomerInvoice in this case
 * @param {GetTableData<DocumentTypes>} params
 * @returns {InvoiceTableDataRow<CommonCells>[]}
 */
export const getInvoiceTableData = ({
  documents,
  isTabletOrMobile,
  language,
  showAddress,
  usesDirectDebit
}: GetTableData<CustomerInvoice>): InvoiceTableDataRow<RegularTableCells>[] => {
  const hasPaymentButtonCellEntry = documents.some((invoice) => {
    const status = getStatus({ document: invoice, usesDirectDebit })
    return invoice.paid || !invoice.paymentUrl || (usesDirectDebit && status.color !== CellColor.NEGATIVE)
  })

  return documents.map((invoice) => {
    const status = getStatus({ document: invoice, usesDirectDebit })
    const hidePaymentButton = invoice.paid || !invoice.paymentUrl || (usesDirectDebit && status.color !== CellColor.NEGATIVE)

    return {
      id: invoice.id,
      uuid: crypto.randomUUID(),
      entries: {
        [InvoiceCells.INVOICE_NUMBER]: getInvoiceNumberCell(invoice),
        [InvoiceCells.DATE]: getDateCell(invoice),
        [InvoiceCells.DOCUMENT_TYPE]: getDocTypeCell(invoice),
        [InvoiceCells.AMOUNT]: getAmountCell(invoice, status, language, showAddress, isTabletOrMobile),
        [InvoiceCells.STATUS]: getStatusCell(
          invoice,
          status,
          showAddress,
          isTabletOrMobile,
          hasPaymentButtonCellEntry && hidePaymentButton ? 2 : 1
        ),
        [InvoiceCells.ADDRESS]: getAddressCell(invoice),
        ...(!hidePaymentButton && {
          [InvoiceCells.PAYMENT_ACTION]: {
            hideOnSmallDesktop: true,
            header: '',
            rawData: invoice.paymentUrl,
            content: (
              <Cell variant={CellVariant.ACTION}>
                <Link
                  representation="button"
                  isFullwidth
                  size="small"
                  as="a"
                  href={invoice.paymentUrl}
                  className={styles['pay-button']}
                  onClick={(event) => {
                    event.stopPropagation()
                    trackEvent({ category: 'billing', object: 'pay_invoice', action: 'start' })
                  }}
                >
                  {i18n.t(($) => $.cells.payOnline, { ns: 'invoices' })}
                </Link>
              </Cell>
            )
          }
        }),

        ...((invoice.pdfUrl || invoice.peppolUrl) && {
          [InvoiceCells.DOWNLOAD_ACTION]: {
            hideOnSmallDesktop: true,
            header: '',
            rawData: {
              pdf: formatInvoiceDownloadUrl(invoice, 'pdf'),
              xml: invoice.peppolUrl ? formatInvoiceDownloadUrl(invoice, 'xml') : undefined
            },
            content: (
              <Cell variant={CellVariant.ACTION}>
                {invoice.peppolUrl ? (
                  <Dropdown
                    customTrigger={
                      <Button
                        size="small"
                        trailingIcon="download"
                        title="Downloads"
                        variant="tertiary"
                        className={styles['dropdown-trigger']}
                      >
                        {invoice.invoiceNumber}
                      </Button>
                    }
                  >
                    {!!invoice.pdfUrl && (
                      <DropdownLinkItem
                        label="PDF"
                        href={formatInvoiceDownloadUrl(invoice, 'pdf')}
                        target="_blank"
                        download
                        onClick={() =>
                          trackEvent({ category: 'billing', object: 'download_invoice', action: 'click' }, { invoiceType: 'pdf' })
                        }
                      />
                    )}
                    <DropdownLinkItem
                      label="Peppol"
                      href={formatInvoiceDownloadUrl(invoice, 'xml')}
                      target="_blank"
                      download
                      onClick={() =>
                        trackEvent({ category: 'billing', object: 'download_invoice', action: 'click' }, { invoiceType: 'peppol' })
                      }
                    />
                  </Dropdown>
                ) : (
                  <Link
                    href={formatInvoiceDownloadUrl(invoice, 'pdf')}
                    as="a"
                    trailingIcon="download"
                    download
                    size="small"
                    representation="button"
                    variant="tertiary"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackEvent({ category: 'billing', object: 'download_invoice', action: 'click' }, { invoiceType: 'pdf' })
                    }}
                  >
                    {invoice.invoiceNumber}
                  </Link>
                )}
              </Cell>
            )
          }
        })
      }
    } satisfies InvoiceTableDataRow<RegularTableCells>
  })
}

/**
 * Get the  invoice table data for a superuser
 * @template DocumentTypes - CustomerInvoice | CustomerPayment in this case
 * @param {GetTableData<DocumentTypes>} params
 * @returns {InvoiceTableDataRow<RegularTableCells>[]}
 */
export const getSuperUserInvoiceTableData = ({
  documents,
  isTabletOrMobile,
  language,
  showAddress,
  usesDirectDebit
}: GetTableData<CustomerInvoice | CustomerPayment>): InvoiceTableDataRow<SuperUserTableCells>[] => {
  return documents.map((document) => {
    const status = getStatus({ document: document, usesDirectDebit })

    return {
      id: document.id,
      uuid: crypto.randomUUID(),
      entries: {
        [InvoiceCells.INVOICE_NUMBER]: getInvoiceNumberCell(document),
        [InvoiceCells.DATE]: getDateCell(document),
        [InvoiceCells.DOCUMENT_TYPE]: getDocTypeCell(document),
        [InvoiceCells.AMOUNT]: getAmountCell(document, status, language, showAddress, isTabletOrMobile),
        [InvoiceCells.STATUS]: getStatusCell(document, status, showAddress, isTabletOrMobile),
        [InvoiceCells.ADDRESS]: getAddressCell(document),
        [InvoiceCells.RUNNING_BALANCE]: {
          content: formatCurrency(document.runningBalance, { language }),
          rawData: document.runningBalance,
          header: i18n.t(($) => $.tableHeaders.outstanding, { ns: 'invoices' })
        },
        [InvoiceCells.MATCHING_NUMBER]: {
          content: <AccountingInfo document={document} />,
          rawData: document.matchingNumber,
          header: i18n.t(($) => $.tableHeaders.accountingInfo, { ns: 'invoices' })
        },
        [InvoiceCells.PAYMENT_METHOD]: {
          content: (
            <Cell
              variant={CellVariant.PAYMENT_METHOD}
              title={
                document.directDebitSent || document.paymentSent
                  ? i18next.t(($) => $.cells[document.paymentSent ? 'paymentSent' : 'directDebitSent'], { ns: 'invoices' })
                  : undefined
              }
            >
              {document.directDebitSent && 'D'}
              {document.paymentSent && 'B'}
            </Cell>
          ),
          rawData: null,
          header: ''
        },
        [InvoiceCells.OPTIONS]: {
          content: <Options document={document} />,
          rawData: null,
          header: ''
        }
      }
    } satisfies InvoiceTableDataRow<SuperUserTableCells>
  })
}

/**
 * Get the  transactions table data for a regular customer
 * @template DocumentTypes - CustomerPayment in this case
 * @param {GetTableData<DocumentTypes>} params
 * @returns {InvoiceTableDataRow<RegularTableCells>[]}
 */
export const getTransactionTableData = ({
  documents,
  isTabletOrMobile,
  language,
  usesDirectDebit
}: GetTableData<CustomerPayment>): InvoiceTableDataRow<TransactionRows>[] => {
  return documents.map((payment) => {
    const status = getStatus({ document: payment, usesDirectDebit })

    return {
      id: payment.id,
      uuid: crypto.randomUUID(),
      //      color: status.color,
      entries: {
        [InvoiceCells.DATE]: getDateCell(payment),
        [InvoiceCells.DOCUMENT_TYPE]: getDocTypeCell(payment),
        [InvoiceCells.AMOUNT]: getAmountCell(payment, status, language, false, isTabletOrMobile),
        [InvoiceCells.STATUS]: getStatusCell(payment, status)
      }
    } satisfies InvoiceTableDataRow<TransactionRows>
  })
}
